import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { ThemeProvider } from '@emotion/react';
import DefaultTheme from '../../../theme/DefaultTheme';

const SPACING_BASE = 4;
const c = DefaultTheme.colors;

/** Theme structure for @ann/web-components (WidgetEmptyErrorPlaceholder, etc.). Uses DefaultTheme colors. */
const widgetGridTheme = {
  palette: {
    background: { neutral: { white: c.white } },
    surface: {
      neutral: {
        white: c.white,
        subtle: c.veryLightGray,
        default: c.greyEight,
        emphasis: c.cottonSeed,
        focus: c.silverChalice,
        darker: c.grey,
        black: c.greyEleven,
      },
      primary: {
        subtle: c.aliceBlue,
        default: c.activeBlue,
        lighter: c.primaryLighter,
        emphasis: c.sapphireBlue,
        darker: c.blueOne,
      },
      error: {
        subtle: c.errorLight,
        default: c.red,
        darker: c.errorDark,
      },
    },
    text: {
      title: c.greyEleven,
      subtitle: c.davysGrey,
      body: c.digital,
      caption: c.davysGrey,
      negative: c.primaryWhite,
      negativeDisable: c.greyEight,
      disabled: c.perlDarkGray,
      label: c.davysGrey,
    },
    icon: {
      neutral: { light: c.primaryWhite, dark: c.davysGrey, black: c.greyEleven },
      primary: { default: c.activeBlue, lighter: c.primaryLighter, darker: c.blueOne },
      error: { default: c.red, darker: c.errorDark },
    },
    border: {
      neutral: { default: c.dividerGray, darker: c.grey, black: c.greyEleven },
    },
  },
  spacing: {
    '0x': '0px', '0.5x': `${SPACING_BASE * 0.5}px`, '1x': `${SPACING_BASE}px`,
    '2x': `${SPACING_BASE * 2}px`, '3x': `${SPACING_BASE * 3}px`, '4x': `${SPACING_BASE * 4}px`,
    '5x': `${SPACING_BASE * 5}px`, '6x': `${SPACING_BASE * 6}px`, '8x': `${SPACING_BASE * 8}px`,
    '10x': `${SPACING_BASE * 10}px`, '12x': `${SPACING_BASE * 12}px`,
    '14x': `${SPACING_BASE * 14}px`, '16x': `${SPACING_BASE * 16}px`,
  },
  typography: {
    fontFamily: { title: DefaultTheme.fonts.family.inter, body: DefaultTheme.fonts.family.inter },
    fontSize: {
      1: DefaultTheme.fontSize.xs, 2: DefaultTheme.fontSize.sm,
      3: DefaultTheme.fontSize.base, 4: DefaultTheme.fontSize.lg, 5: DefaultTheme.fonts.size.s1,
    },
    fontWeight: { regular: 400, medium: 500, semibold: 600, bold: 700 },
  },
  radius: {
    'radius-s': DefaultTheme.borderRadius.sm,
    'radius-m': DefaultTheme.borderRadius.base,
    'radius-l': DefaultTheme.borderRadius.lg,
  },
};

const WidgetGridContext = createContext(null);

export const WidgetGridProvider = ({
  scopeType,
  module,
  scopeContext,
  // Legacy props (kept for backward compatibility during migration)
  contextEntity,
  contextId,
  userId = '',
  onNavigate,
  onWidgetAction,
  onCreateNoteClick,
  isEditMode = true,
  children,
}) => {
  const [dashboardUuid, setDashboardUuid] = useState(null);
  const [activeFilters, setActiveFilters] = useState({});

  const updateActiveFilters = useCallback((filters) => {
    setActiveFilters(filters);
  }, []);

  const value = useMemo(
    () => ({
      scopeType: scopeType || null,
      module: module || null,
      scopeContext: scopeContext || null,
      dashboardUuid,
      setDashboardUuid,
      activeFilters,
      updateActiveFilters,
      // Legacy (still used by existing consumers during migration)
      contextEntity: contextEntity || null,
      contextId: contextId ?? null,
      userId,
      onNavigate: onNavigate || (() => {}),
      onWidgetAction: onWidgetAction || (() => {}),
      onCreateNoteClick: onCreateNoteClick || null,
      isEditMode,
    }),
    [
      scopeType, module, scopeContext,
      dashboardUuid, activeFilters, updateActiveFilters,
      contextEntity, contextId, userId,
      onNavigate, onWidgetAction, onCreateNoteClick, isEditMode,
    ]
  );

  return (
    <ThemeProvider theme={widgetGridTheme}>
      <WidgetGridContext.Provider value={value}>
        {children}
      </WidgetGridContext.Provider>
    </ThemeProvider>
  );
};

export const useWidgetGridContext = () => {
  const context = useContext(WidgetGridContext);
  if (!context) {
    throw new Error(
      'useWidgetGridContext must be used within a WidgetGridProvider'
    );
  }
  return context;
};
